import { useSnackbar } from "notistack";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { binaryToUint8Array, pickFileName } from "../utilities/tachographFiles";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { safeFilename } from "@/utilities/mediaSafety";
import { downloadBlob } from "@/utilities/xlsx";
import { createZip, type ZipEntry } from "@/utilities/xlsx/zip";

/** The file content is only fetched for the files a user really wants. */
export function useTachographFileDownload() {
  const { geotabClient } = useGeotabClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchFileContent = async (
    fileId: string,
  ): Promise<{ name: string; content: Uint8Array } | null> => {
    const [file] = await geotabClient.get(
      "TachographDataFile",
      { id: fileId, includeBinaryData: true },
      1,
    );

    const content = binaryToUint8Array(file?.binaryData);
    if (!content) return null;

    const fallbackName = `${fileId}.ddd`;
    return { name: safeFilename(pickFileName(file) || fallbackName, fallbackName), content };
  };

  const downloadFiles = async (fileIds: string[]) => {
    if (fileIds.length === 0 || isDownloading) return;

    setIsDownloading(true);
    let failed = 0;
    const successful: { name: string; content: Uint8Array }[] = [];

    try {
      for (const fileId of fileIds) {
        try {
          const result = await fetchFileContent(fileId);
          if (result) {
            successful.push(result);
          } else {
            failed += 1;
          }
        } catch (error) {
          console.error(`Failed to download tachograph file ${fileId}`, error);
          failed += 1;
        }
      }
    } finally {
      setIsDownloading(false);
    }

    if (successful.length === 1) {
      const { name, content } = successful[0];
      downloadBlob(
        new Blob([content], { type: "application/octet-stream" }),
        name,
      );
    } else if (successful.length > 1) {
      const entries: ZipEntry[] = successful.map(({ name, content }) => ({ name, data: content }));
      const zipped = createZip(entries);
      downloadBlob(new Blob([zipped], { type: "application/zip" }), "tachograph-files.zip");
    }

    if (failed > 0) {
      enqueueSnackbar(t("tachograph.downloadFailed", { count: failed }), { variant: "warning" });
    }
  };

  return { downloadFiles, isDownloading };
}
